import {
  beruehrtAbgeschlosseneWoche,
  istAbgeschlosseneWoche,
  leseStundenEingabe,
} from '@asas/domain/belegung';
import { compareIsoWeeks } from '@asas/domain/iso-week';
import type {
  Belegung,
  IsoWeek,
  Ladefenster,
  OptimisticLockConflict,
  Permission,
  WeekGrid,
} from '@asas/domain';

import { stundenAnzeige, wochenkopf } from './wochenraster';

/**
 * Reine Hilfen fürs Planen im Wochenraster (AC-403) — ohne React, ohne Netz.
 *
 * Was eine Eingabe bedeutet, wann eine Woche abgeschlossen ist und wann eine
 * Bestätigung fällig ist, sagt `@asas/domain/belegung` — dieselben Funktionen,
 * mit denen der Server prüft. Hier steht nur, wie die Oberfläche sie anwendet.
 * Gerechnet wird nichts: Summen und Überlast kommen nach dem Speichern vom
 * Server (Raster neu laden, AK 1, AK 4). Stunden bleiben Zeichenketten.
 */

/** Das Recht der Prozeduren `belegungen.*` (H2). */
export const PLANUNGSRECHT: Permission = 'resource_allocation:write';
/** Das Recht für abgeschlossene Wochen (AK 7). */
export const NACHTRAGSRECHT: Permission = 'resource_allocation:edit_past';

/**
 * Bietet die Ansicht das Planen an? Nur mit `resource_allocation:write` — sonst
 * schriebe der erste Klick ein 403 ins Audit-Log (Muster `darfFachgruppenPflegen`).
 */
export function darfPlanen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(PLANUNGSRECHT);
}

/** Bietet die Ansicht abgeschlossene Wochen zum Bearbeiten an (AK 7)? */
export function darfNachtragen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(PLANUNGSRECHT) && rechte.has(NACHTRAGSRECHT);
}

/** „8.00" → „8,00"? Nein: die Anzeige wie im Raster, „8" bzw. „7,5". */
export function eingabeText(planHours: string): string {
  return stundenAnzeige(planHours);
}

/** `'7.5'` und `'7.50'` sind derselbe Wert — Vergleich über die Zeichenkette, nie `Number`. */
function gleicheStunden(a: string, b: string): boolean {
  const form = (wert: string): string => {
    const [ganz = '0', teil = ''] = wert.split('.');
    return `${ganz.replace(/^0+(?=\d)/, '')}.${teil.padEnd(2, '0')}`;
  };

  return form(a) === form(b);
}

/**
 * Was beim Verlassen des Felds geschieht (AK 1, AK 5, E2):
 *
 *   unveraendert  nichts senden
 *   loeschen      die Zelle wurde geleert oder auf null gesetzt
 *   aendern       ein neuer gültiger Wert
 *   ungueltig     abweisen, der bisherige Wert bleibt stehen
 */
export type FeldAktion =
  | { readonly art: 'unveraendert' }
  | { readonly art: 'loeschen' }
  | { readonly art: 'aendern'; readonly stunden: string }
  | { readonly art: 'ungueltig' };

export function feldAktion(text: string, gespeichert: string): FeldAktion {
  const eingabe = leseStundenEingabe(text);

  if (eingabe.art === 'ungueltig') return { art: 'ungueltig' };
  if (eingabe.art === 'leer') return { art: 'loeschen' };
  if (gleicheStunden(eingabe.stunden, gespeichert)) return { art: 'unveraendert' };
  return { art: 'aendern', stunden: eingabe.stunden };
}

export const UNGUELTIGE_STUNDEN =
  'Bitte eine Zahl zwischen 0 und 999,99 eingeben (höchstens zwei Nachkommastellen). Der bisherige Wert bleibt.';

/** Das geladene Fenster des Rasters, wie es jeder Schreibweg mitschickt (AC-410). */
export function geladenesFenster(raster: WeekGrid): Ladefenster {
  return { from: raster.editableFrom, weeks: raster.wochen.length };
}

/**
 * Wohin sich eine Belegung verschieben lässt (AK 2): jede Woche des geladenen
 * Fensters außer ihrer eigenen; abgeschlossene nur mit dem Nachtragsrecht (AK 7).
 */
export function verschiebeZiele(
  raster: WeekGrid,
  woche: IsoWeek,
  laufendeWoche: IsoWeek,
  nachtragen: boolean,
): IsoWeek[] {
  return raster.wochen.filter(
    (ziel) =>
      compareIsoWeeks(ziel, woche) !== 0 &&
      (nachtragen || !istAbgeschlosseneWoche(ziel, laufendeWoche)),
  );
}

/** Ist die Woche ohne Nachtragsrecht gesperrt (AK 7)? Dann bietet die Zelle nichts an. */
export function wocheGesperrt(
  woche: IsoWeek,
  laufendeWoche: IsoWeek,
  nachtragen: boolean,
): boolean {
  return !nachtragen && istAbgeschlosseneWoche(woche, laufendeWoche);
}

/**
 * Die Begründung, wenn der Schreibweg eine abgeschlossene Woche berührt — sonst
 * nichts (der Server speichert sie dann auch nicht). `fehlt`, wenn sie nötig
 * wäre, aber leer ist: dann wird nicht gesendet.
 */
export function begruendungFuer(
  wochen: readonly IsoWeek[],
  laufendeWoche: IsoWeek,
  text: string,
): { readonly begruendung?: string } | 'fehlt' {
  if (!beruehrtAbgeschlosseneWoche(wochen, laufendeWoche)) return {};

  const begruendung = text.trim();
  return begruendung === '' ? 'fehlt' : { begruendung };
}

export const BEGRUENDUNG_FEHLT = 'Eine abgeschlossene Woche lässt sich nur mit Begründung ändern.';

/** Die Bezeichnung einer Belegung in der Liste: Projekt, gegebenenfalls Arbeitspaket. */
export function belegungsTitel(belegung: Belegung): string {
  return belegung.workPackageLabel === null
    ? belegung.projectLabel
    : `${belegung.projectLabel} · ${belegung.workPackageLabel}`;
}

function wocheText(woche: IsoWeek): string {
  return wochenkopf(woche, undefined);
}

/**
 * Beide Werte eines Konflikts in Worten (AC-409, AK 2): was inzwischen
 * gespeichert ist und was eingegeben wurde. Der Mensch entscheidet.
 */
export function konfliktText(konflikt: OptimisticLockConflict): string {
  const server = konflikt.serverState as { planHours?: string; woche?: IsoWeek };
  const versuch = konflikt.attemptedState as { planHours?: string; woche?: IsoWeek };
  const gespeichert = [
    server.planHours === undefined ? null : `${stundenAnzeige(server.planHours)} h`,
    server.woche === undefined ? null : wocheText(server.woche),
  ]
    .filter((teil) => teil !== null)
    .join(' in ');
  const eingabe =
    versuch.planHours !== undefined
      ? `${stundenAnzeige(versuch.planHours)} h`
      : versuch.woche !== undefined
        ? `verschieben nach ${wocheText(versuch.woche)}`
        : 'löschen';

  return `Inzwischen gespeichert: ${gespeichert} · Ihre Eingabe: ${eingabe}`;
}

/**
 * Die Warnung nach dem Speichern (AK 4): Überlast in einer der betroffenen
 * Wochen laut neu geladenem Raster — ein Hinweis, keine Blockade. `null`, wenn
 * keine der Wochen überlastet ist.
 */
export function ueberlastHinweis(
  raster: WeekGrid,
  employeeId: string,
  wochen: readonly IsoWeek[],
): string | null {
  const zellen = raster.zeilen.find((zeile) => zeile.employeeId === employeeId)?.zellen ?? [];
  const ueberlastet = zellen.filter(
    (zelle) =>
      !zelle.locked &&
      zelle.overload &&
      wochen.some((woche) => compareIsoWeeks(woche, zelle) === 0),
  );

  if (ueberlastet.length === 0) return null;

  return `Gespeichert — Achtung, Überlast: ${ueberlastet
    .map((zelle) =>
      zelle.locked
        ? ''
        : `${wocheText(zelle)} ${stundenAnzeige(zelle.plannedHours)} von ${stundenAnzeige(zelle.availableHours)} h`,
    )
    .join(', ')}.`;
}

// --- Schreibwarteschlange (AC-409, AK 4) --------------------------------------------

/** Ein versionierter Schreibauftrag; Anlegen hat keine Version und läuft nicht hierüber. */
export type Planungsauftrag =
  | {
      readonly art: 'aendern';
      readonly id: string;
      readonly version: number;
      readonly planHours: string;
      readonly fenster: Ladefenster;
      readonly stundenBestaetigt?: boolean;
      readonly begruendung?: string;
    }
  | {
      readonly art: 'verschieben';
      readonly id: string;
      readonly version: number;
      readonly nachWoche: IsoWeek;
      readonly fenster: Ladefenster;
      readonly begruendung?: string;
    }
  | {
      readonly art: 'loeschen';
      readonly id: string;
      readonly version: number;
      readonly fenster: Ladefenster;
      readonly begruendung?: string;
    };

function istObjekt(wert: unknown): wert is Record<string, unknown> {
  return typeof wert === 'object' && wert !== null && !Array.isArray(wert);
}

function istWoche(wert: unknown): wert is IsoWeek {
  return (
    istObjekt(wert) &&
    Number.isInteger(wert.isoYear) &&
    Number.isInteger(wert.isoWeek) &&
    Object.keys(wert).length === 2
  );
}

function istFenster(wert: unknown): wert is Ladefenster {
  return (
    istObjekt(wert) &&
    istWoche(wert.from) &&
    Number.isInteger(wert.weeks) &&
    Object.keys(wert).length === 2
  );
}

/**
 * Prüfer für einen geborgenen Auftrag (Text aus dem Browserspeicher, nicht
 * vertrauenswürdig): nur die bekannte Form, sonst `null`. Ohne Zod (AC-202-1);
 * den Inhalt prüft der Server ohnehin.
 */
export function pruefePlanungsauftrag(roh: unknown): Planungsauftrag | null {
  if (!istObjekt(roh)) return null;

  const basis =
    typeof roh.id === 'string' &&
    Number.isInteger(roh.version) &&
    istFenster(roh.fenster) &&
    (roh.begruendung === undefined || typeof roh.begruendung === 'string');
  if (!basis) return null;

  switch (roh.art) {
    case 'aendern':
      return typeof roh.planHours === 'string' &&
        (roh.stundenBestaetigt === undefined || typeof roh.stundenBestaetigt === 'boolean')
        ? (roh as Planungsauftrag)
        : null;
    case 'verschieben':
      return istWoche(roh.nachWoche) ? (roh as Planungsauftrag) : null;
    case 'loeschen':
      return roh as Planungsauftrag;
    default:
      return null;
  }
}

/** Die Eingabe der Prozedur: der Auftrag ohne seine Art. */
export function auftragEingabe<A extends Planungsauftrag>(auftrag: A): Omit<A, 'art'> {
  const eingabe: Record<string, unknown> = { ...auftrag };
  delete eingabe.art;
  return eingabe as Omit<A, 'art'>;
}

/** Ein geborgener Auftrag in Worten, damit der Mensch ihn wiedererkennt. */
export function auftragText(auftrag: Planungsauftrag): string {
  switch (auftrag.art) {
    case 'aendern':
      return `${stundenAnzeige(auftrag.planHours)} h eintragen`;
    case 'verschieben':
      return `nach ${wocheText(auftrag.nachWoche)} verschieben`;
    case 'loeschen':
      return 'löschen';
  }
}
